import type React from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';

interface LabelProps {
  $valignCenter?: boolean;
}

export const Label = styled.label<LabelProps>`
  position: relative;
  display: flex;
  align-items: flex-start;
  font-size: var(--text-size);
  line-height: var(--text-line-height);
  user-select: none;
  cursor: default;

  &::before {
    content: '';
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--checkbox-size);
    height: var(--checkbox-size);
    margin-top: calc((var(--checkbox-size) - var(--text-size) * var(--text-line-height)) / -2);
    background-color: var(--c-input-selection-bg);
    color: var(--c-base-fg);
    font-size: 1rem;
    border-radius: 2px;
    cursor: pointer;
  }

  ${({ $valignCenter }) =>
    $valignCenter &&
    css`
      align-items: center;

      &::before {
        margin-top: 0;
      }
    `}
`;

interface WrapperProps {
  $error?: boolean;
  $textSize?: 'xsmall' | 'small';
}

export const Wrapper = styled.div.attrs(() => ({
  role: 'checkbox',
}))<WrapperProps>`
  --checkbox-size: ${size(2)};
  --text-line-height: ${(props) => props.theme.font.lineHeight.main};
  position: relative;

  ${({ $textSize }) =>
    $textSize === 'xsmall'
      ? css`
          --text-size: ${(props) => props.theme.font.size.xsmall};
        `
      : $textSize === 'small'
        ? css`
            --text-size: ${(props) => props.theme.font.size.small};
          `
        : css`
            --text-size: ${(props) => props.theme.font.size.regular};
          `};

  ${({ $error }) =>
    $error &&
    css`
      ${Label} {
        ${LabelText} {
          color: var(--c-input-error-text);
        }
      }
    `};
`;

type InputProps = Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'ref'
>;

export const Input = styled(
  forwardRef<HTMLInputElement, InputProps>((props, ref) => (
    <input
      {...props}
      ref={ref}
      className={`${props.checked ? 'checked' : undefined} ${props.className}`}
    />
  )),
).attrs(({ type, checked }) => ({
  checked,
  type: type || 'checkbox',
}))`
  position: absolute;
  width: 1px;
  height: 1px;
  clip-path: inset(50%);
  overflow: hidden;
  white-space: nowrap;

  &[disabled] {
    & + ${Label} {
      opacity: 0.37;
      cursor: not-allowed;
    }
  }

  &:checked,
  &.checked {
    & + ${Label} {
      &::before {
        background-color: var(--checkbox-checked-bg, var(--c-input-selection-bg-active));
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="16" height="16"><path d="M12.3 3.3 6 9.6 3.7 7.3c-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4l3 3c.4.4 1 .4 1.4 0l7-7c.4-.4.4-1 0-1.4-.4-.4-1-.4-1.4 0z" fill="%23FFFFFF"/></svg>');
        background-repeat: no-repeat;
        background-size: calc(var(--checkbox-size) * 0.625);
        background-position: 50%;
        ${(props) => props.color && `background-color: ${props.color};`}
      }
    }
  }

  @supports selector(:focus-visible) {
    &:focus-visible {
      & ~ ${Label} {
        &::before {
          outline: max(2px, 0.08em) solid var(--c-focus);
          outline-offset: max(2px, 0.08em);
        }
      }
    }
  }

  // Safari & IE11
  @supports not selector(:focus-visible) {
    &:focus {
      & ~ ${Label} {
        &::before {
          outline: max(2px, 0.08em) solid var(--c-focus);
          outline-offset: max(2px, 0.08em);
        }
      }
    }
  }
`;

interface LabelTextProps {
  $extraSpacing?: boolean; // Add extra spacing between checkbox and text
  $textTone?: 'alt';
}

export const LabelText = styled.div<LabelTextProps>`
  padding-left: ${size(1.5)};

  ${({ $extraSpacing }) =>
    $extraSpacing &&
    css`
      padding-left: ${size(2)};
    `}

  ${({ $textTone }) =>
    $textTone === 'alt'
      ? css`
          color: var(--c-text-muted);
        `
      : css`
          color: var(--c-text-main);
        `}
`;
